import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { adminRouteParams, replaceAdminRouteParams } from "../lib/hashRoute";
import { PedagogicalContentWorkspace } from "./PedagogicalContentWorkspace";
import {
  addFormationAdmin,
  createFormation,
  listAccessibleFormations,
  listFormationAdmins,
  removeFormationAdmin,
  softDeleteFormation,
  updateFormationMetadata,
} from "../services/formationService";
import {
  CURRENT_PUBLICATION_TAG,
  deleteFormationContentTag,
  readFormationContentHistory,
  syncFormationContentSnapshot,
  type FormationContentHistory,
} from "../services/formationContentVersioningService";
import {
  buildFormationTransportBundle,
  importFormationTransportBundle,
  parseFormationTransportBundle,
  serializeFormationTransportBundle,
  validateFormationTransportBundle,
} from "../services/formationTransportService";
import type { Formation, FormationAdmin } from "../types/formation";
import type { FormationImportReport } from "../types/transport";

function formatDate(value: Date | null): string {
  if (!value) {
    return "Non disponible";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

function formatBytes(value: number): string {
  if (value < 1024) {
    return `${value} o`;
  }

  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} Ko`;
  }

  return `${(value / (1024 * 1024)).toFixed(1)} Mo`;
}

function canCreateFormation(
  canCreateFormationClaim: boolean,
  isGlobalAdmin: boolean,
): boolean {
  return isGlobalAdmin || canCreateFormationClaim;
}

function canSoftDeleteFormation(
  formation: Formation | null,
  isGlobalAdmin: boolean,
  uid: string | undefined,
): boolean {
  return Boolean(formation && uid && (isGlobalAdmin || formation.authorUid === uid));
}

function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
}

function downloadJsonFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

type FormationEditorMode = "create" | "edit";
type FormationDetailTab = "transport" | "versioning" | "rights" | "content";

const formationDetailTabs: Array<{
  id: FormationDetailTab;
  label: string;
  summary: string;
}> = [
  {
    id: "transport",
    label: "Import / export",
    summary: "Transport JSON",
  },
  {
    id: "versioning",
    label: "Versioning",
    summary: "Object VCS",
  },
  {
    id: "rights",
    label: "Droits",
    summary: "Administrateurs",
  },
  {
    id: "content",
    label: "Contenus",
    summary: "Objets pédagogiques",
  },
];

function readFormationDetailTabParam(): FormationDetailTab {
  const tab = adminRouteParams().get("formationTab");

  return formationDetailTabs.some((item) => item.id === tab)
    ? (tab as FormationDetailTab)
    : "transport";
}

export function FormationWorkspace() {
  const { claims, isGlobalAdmin, user } = useAuth();
  const [adminUid, setAdminUid] = useState("");
  const [admins, setAdmins] = useState<FormationAdmin[]>([]);
  const [description, setDescription] = useState("");
  const [contentHistory, setContentHistory] =
    useState<FormationContentHistory | null>(null);
  const [formations, setFormations] = useState<Formation[]>([]);
  const [activeDetailTab, setActiveDetailTab] = useState<FormationDetailTab>(
    readFormationDetailTabParam,
  );
  const [formationSearch, setFormationSearch] = useState("");
  const [isLoadingContentHistory, setIsLoadingContentHistory] = useState(false);
  const [isDeletingTag, setIsDeletingTag] = useState(false);
  const [importReport, setImportReport] =
    useState<FormationImportReport | null>(null);
  const [editorMode, setEditorMode] = useState<FormationEditorMode | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncingContent, setIsSyncingContent] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedFormationId, setSelectedFormationId] = useState<string | null>(
    null,
  );
  const [title, setTitle] = useState("");

  const selectedFormation = useMemo(
    () =>
      formations.find((formation) => formation.id === selectedFormationId) ??
      null,
    [formations, selectedFormationId],
  );
  const filteredFormations = useMemo(() => {
    const search = formationSearch.trim().toLowerCase();

    if (!search) {
      return formations;
    }

    return formations.filter((formation) =>
      `${formation.title} ${formation.description}`.toLowerCase().includes(search),
    );
  }, [formationSearch, formations]);
  const mayCreateFormation = canCreateFormation(
    claims.canCreateFormation,
    isGlobalAdmin,
  );
  const maySoftDeleteFormation = canSoftDeleteFormation(
    selectedFormation,
    isGlobalAdmin,
    user?.uid,
  );

  useEffect(() => {
    function handleHashChange() {
      const params = adminRouteParams();
      const nextTab = readFormationDetailTabParam();
      const nextFormationId = params.get("formation");

      setActiveDetailTab(nextTab);
      setSelectedFormationId((currentId) =>
        nextFormationId &&
        formations.some((formation) => formation.id === nextFormationId)
          ? nextFormationId
          : currentId,
      );
    }

    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handleHashChange);
    };
  }, [formations]);

  useEffect(() => {
    if (!user) {
      setFormations([]);
      setEditorMode(null);
      setSelectedFormationId(null);
      replaceAdminRouteParams({ formation: null });
      return;
    }

    setIsLoading(true);
    void listAccessibleFormations({
      isGlobalAdmin,
      uid: user.uid,
    })
      .then((nextFormations) => {
        const requestedFormationId = adminRouteParams().get("formation");
        setFormations(nextFormations);
        setSelectedFormationId((currentId) => {
          if (
            requestedFormationId &&
            nextFormations.some((formation) => formation.id === requestedFormationId)
          ) {
            return requestedFormationId;
          }

          if (currentId && nextFormations.some((formation) => formation.id === currentId)) {
            return currentId;
          }

          return nextFormations[0]?.id ?? null;
        });
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger les formations accessibles.");
      })
      .finally(() => setIsLoading(false));
  }, [isGlobalAdmin, user]);

  useEffect(() => {
    replaceAdminRouteParams({ formation: selectedFormationId });
  }, [selectedFormationId]);

  useEffect(() => {
    setTitle(selectedFormation?.title ?? "");
    setDescription(selectedFormation?.description ?? "");

    if (!selectedFormation) {
      setAdmins([]);
      setContentHistory(null);
      return;
    }

    setIsLoadingContentHistory(true);
    void Promise.all([
      listFormationAdmins(selectedFormation.id),
      readFormationContentHistory(selectedFormation.id),
    ])
      .then(([nextAdmins, nextContentHistory]) => {
        setAdmins(nextAdmins);
        setContentHistory(nextContentHistory);
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger le détail de la formation.");
      })
      .finally(() => setIsLoadingContentHistory(false));
  }, [selectedFormation]);

  async function refreshContentHistory(formationId: string) {
    const nextHistory = await readFormationContentHistory(formationId);
    setContentHistory(nextHistory);
  }

  async function handleCreateFormation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!user || !mayCreateFormation) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const nextFormation = await createFormation({
        actorUid: user.uid,
        draft: {
          description,
          title,
        },
      });

      setFormations((current) => [nextFormation, ...current]);
      setEditorMode(null);
      setSelectedFormationId(nextFormation.id);
      setMessage("Formation créée.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible de créer la formation.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleUpdateFormation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!user || !selectedFormation) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const nextFormation = await updateFormationMetadata({
        actorUid: user.uid,
        formationId: selectedFormation.id,
        patch: {
          description,
          title,
        },
      });

      setFormations((current) =>
        current.map((formation) =>
          formation.id === nextFormation.id ? nextFormation : formation,
        ),
      );
      setEditorMode(null);
      setMessage("Formation mise à jour.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible de modifier la formation.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSoftDeleteFormation(targetFormation: Formation | null = selectedFormation) {
    if (
      !user ||
      !targetFormation ||
      !canSoftDeleteFormation(targetFormation, isGlobalAdmin, user.uid)
    ) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      await softDeleteFormation({
        actorUid: user.uid,
        formationId: targetFormation.id,
      });
      setFormations((current) =>
        current.filter((formation) => formation.id !== targetFormation.id),
      );
      setEditorMode(null);
      setSelectedFormationId((currentId) =>
        currentId === targetFormation.id ? null : currentId,
      );
      setMessage("Formation supprimée logiquement.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible de supprimer logiquement la formation.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAddAdmin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!user || !selectedFormation) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const nextAdmin = await addFormationAdmin({
        actorUid: user.uid,
        draft: {
          uid: adminUid,
        },
        formation: selectedFormation,
      });

      setAdmins((current) => [
        nextAdmin,
        ...current.filter((admin) => admin.uid !== nextAdmin.uid),
      ]);
      setAdminUid("");
      setMessage("Administrateur ajouté à la formation.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible d'ajouter cet administrateur.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRemoveAdmin(targetUid: string) {
    if (!user || !selectedFormation) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      await removeFormationAdmin({
        actorUid: user.uid,
        formationId: selectedFormation.id,
        targetUid,
      });
      setAdmins((current) => current.filter((admin) => admin.uid !== targetUid));
      setMessage("Administrateur retiré de la formation.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible de retirer cet administrateur.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSyncContentSnapshot() {
    if (!user || !selectedFormation) {
      return;
    }

    setIsSyncingContent(true);
    setMessage(null);

    try {
      const result = await syncFormationContentSnapshot({
        actorUid: user.uid,
        formationId: selectedFormation.id,
        message: `Snapshot contenu - ${selectedFormation.title}`,
      });
      await refreshContentHistory(selectedFormation.id);
      setMessage(
        result.initialized
          ? "Dépôt de contenu initialisé."
          : result.revision
            ? "Snapshot de contenu créé."
            : "Contenu déjà synchronisé.",
      );
    } catch (error) {
      console.error(error);
      setMessage("Impossible de synchroniser le contenu versionné.");
    } finally {
      setIsSyncingContent(false);
    }
  }

  async function handleDeleteContentTag(tagName: string, revision: number) {
    if (!user || !selectedFormation) {
      return;
    }

    setIsDeletingTag(true);
    setMessage(null);

    try {
      await deleteFormationContentTag({
        actorUid: user.uid,
        expectedRevision: revision,
        formationId: selectedFormation.id,
        tagName,
      });
      await refreshContentHistory(selectedFormation.id);
      setMessage("Tag supprimé.");
    } catch (error) {
      console.error(error);
      setMessage(
        error instanceof Error
          ? error.message
          : "Impossible de supprimer ce tag.",
      );
    } finally {
      setIsDeletingTag(false);
    }
  }

  async function handleExportFormation(targetFormation: Formation | null = selectedFormation) {
    if (!targetFormation) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const bundle = await buildFormationTransportBundle(targetFormation);
      downloadJsonFile(
        `${slugify(targetFormation.title) || "formation"}-export-v1.json`,
        serializeFormationTransportBundle(bundle),
      );
      setMessage("Export formation préparé.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible d'exporter cette formation.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleImportFormation(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";

    if (!file || !user || !mayCreateFormation) {
      return;
    }

    setIsImporting(true);
    setMessage(null);
    setImportReport(null);

    try {
      const bundle = parseFormationTransportBundle(await file.text());
      const validation = validateFormationTransportBundle(bundle);

      if (validation.status === "invalid") {
        setImportReport(validation);
        setMessage("Import refusé : le format contient des erreurs.");
        return;
      }

      const report = await importFormationTransportBundle({
        actorUid: user.uid,
        bundle,
      });
      const nextFormations = await listAccessibleFormations({
        isGlobalAdmin,
        uid: user.uid,
      });
      setFormations(nextFormations);
      setEditorMode(null);
      setSelectedFormationId(report.createdFormationId);
      setImportReport(report);
      setMessage("Formation importée avec remapping des IDs.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible d'importer ce fichier.");
    } finally {
      setIsImporting(false);
    }
  }

  function openCreateEditor() {
    setEditorMode("create");
    setSelectedFormationId(null);
    setTitle("");
    setDescription("");
    setMessage(null);
  }

  function openEditEditor(formation: Formation) {
    setEditorMode("edit");
    setSelectedFormationId(formation.id);
    setTitle(formation.title);
    setDescription(formation.description);
    setMessage(null);
  }

  function closeEditor() {
    setEditorMode(null);
    setTitle(selectedFormation?.title ?? "");
    setDescription(selectedFormation?.description ?? "");
  }

  function selectFormation(formationId: string) {
    setSelectedFormationId(formationId);
    replaceAdminRouteParams({ formation: formationId });
  }

  function openDetailTab(tab: FormationDetailTab) {
    setActiveDetailTab(tab);
    replaceAdminRouteParams({ formationTab: tab });
  }

  return (
    <section className="formation-workspace">
      <article className="panel panel-wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Formations</p>
            <h2>Formations canoniques</h2>
          </div>
          <div className="actions">
            <button
              className="button button-primary"
              disabled={!mayCreateFormation}
              onClick={openCreateEditor}
              type="button"
            >
              Nouvelle formation
            </button>
            <label className="button button-secondary file-button">
              Importer
              <input
                accept="application/json,.json"
                disabled={!mayCreateFormation || isImporting}
                onChange={(event) => void handleImportFormation(event)}
                type="file"
              />
            </label>
          </div>
        </div>
        <div className="list-toolbar">
          <label className="field list-filter">
            <span>Filtrer</span>
            <input
              onChange={(event) => setFormationSearch(event.target.value)}
              placeholder="Titre ou description"
              type="search"
              value={formationSearch}
            />
          </label>
          <div className="list-summary">
            <strong>{filteredFormations.length}</strong>
            <span>formation(s)</span>
          </div>
        </div>
        {isLoading ? <p>Chargement des formations...</p> : null}
        {filteredFormations.length > 0 ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Formation</th>
                  <th scope="col">Description</th>
                  <th scope="col">Mise à jour</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFormations.map((formation) => {
                  const isSelected = formation.id === selectedFormationId;

                  return (
                    <tr className={isSelected ? "row-selected" : ""} key={formation.id}>
                      <td>
                        <button
                          className="table-title-button"
                          onClick={() => selectFormation(formation.id)}
                          type="button"
                        >
                          <strong>{formation.title}</strong>
                          <span>{formation.id}</span>
                        </button>
                      </td>
                      <td>{formation.description || "Aucune description"}</td>
                      <td>{formatDate(formation.updatedAt)}</td>
                      <td>
                        <div className="row-actions">
                          <button
                            className="button button-secondary button-small"
                            onClick={() => selectFormation(formation.id)}
                            type="button"
                          >
                            Ouvrir
                          </button>
                          <button
                            className="button button-secondary button-small"
                            onClick={() => openEditEditor(formation)}
                            type="button"
                          >
                            Modifier
                          </button>
                          <button
                            className="button button-secondary button-small"
                            disabled={isSaving}
                            onClick={() => void handleExportFormation(formation)}
                            type="button"
                          >
                            Exporter
                          </button>
                          <button
                            className="button button-secondary button-small"
                            disabled={
                              isSaving ||
                              !canSoftDeleteFormation(formation, isGlobalAdmin, user?.uid)
                            }
                            onClick={() => void handleSoftDeleteFormation(formation)}
                            type="button"
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Aucune formation ne correspond aux filtres.</p>
        )}
        {message ? (
          <div className="alert alert-info" role="status">
            {message}
          </div>
        ) : null}
      </article>

      {selectedFormation ? (
        <>
          <article className="panel panel-wide">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Détail formation</p>
                <h2>{selectedFormation.title}</h2>
              </div>
            </div>
            <div className="detail-tabs" role="tablist" aria-label="Sections formation">
              {formationDetailTabs.map((tab) => (
                <button
                  aria-selected={activeDetailTab === tab.id}
                  className={
                    activeDetailTab === tab.id
                      ? "detail-tab detail-tab-active"
                      : "detail-tab"
                  }
                  key={tab.id}
                  onClick={() => openDetailTab(tab.id)}
                  type="button"
                >
                  <span>{tab.label}</span>
                  <small>{tab.summary}</small>
                </button>
              ))}
            </div>
          </article>

          {activeDetailTab === "transport" ? (
            <article className="panel panel-wide">
              <p className="eyebrow">Import / export</p>
              <h2>Transport formation</h2>
              <div className="actions">
                <button
                  className="button button-secondary"
                  disabled={!selectedFormation || isSaving}
                  onClick={() => void handleExportFormation()}
                  type="button"
                >
                  Exporter la formation
                </button>
                <label className="button button-primary file-button">
                  Importer un fichier JSON
                  <input
                    accept="application/json,.json"
                    disabled={!mayCreateFormation || isImporting}
                    onChange={(event) => void handleImportFormation(event)}
                    type="file"
                  />
                </label>
              </div>
              <p className="hint">
                Le format V1 transporte la formation et ses contenus, exclut runtime et
                ACL, puis remappe les IDs à l'import.
              </p>
              {importReport ? (
                <div className="maintenance-block">
                  <h3>Rapport d'import</h3>
                  <div className="capability-strip">
                    <span>Statut : {importReport.status}</span>
                    <span>{importReport.createdDeckCount} deck(s)</span>
                    <span>{importReport.createdQuestionnaireCount} questionnaire(s)</span>
                    <span>{importReport.createdExerciseCount} exercice(s)</span>
                  </div>
                  {importReport.issues.length > 0 ? (
                    <ul className="compact-list">
                      {importReport.issues.map((item, index) => (
                        <li key={`${item.path}-${index}`}>
                          <span>{item.severity}</span>
                          <small>
                            {item.path} - {item.message}
                          </small>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>Aucune anomalie détectée.</p>
                  )}
                </div>
              ) : null}
            </article>
          ) : null}

          {activeDetailTab === "versioning" ? (
          <article className="panel panel-wide">
            <p className="eyebrow">Versioning contenu</p>
            <h2>Snapshot Object VCS</h2>
            <dl className="details-list">
              <div>
                <dt>Graph version</dt>
                <dd>{contentHistory?.graphIdentity.graphVersion ?? "Non disponible"}</dd>
              </div>
              <div>
                <dt>Schema fingerprint</dt>
                <dd className="mono-break">
                  {contentHistory?.graphIdentity.schemaFingerprint ?? "Non disponible"}
                </dd>
              </div>
              <div>
                <dt>Algorithme schema</dt>
                <dd>
                  {contentHistory?.graphIdentity.schemaFingerprintAlgorithm ??
                    "Non disponible"}
                </dd>
              </div>
              <div>
                <dt>Branche active</dt>
                <dd>{contentHistory?.head?.branchName ?? "Non initialisée"}</dd>
              </div>
              <div>
                <dt>Révision HEAD</dt>
                <dd>
                  {contentHistory?.head?.headRevision
                    ? `#${contentHistory.head.headRevision}`
                    : "Aucune révision"}
                </dd>
              </div>
              <div>
                <dt>Historique</dt>
                <dd>
                  {isLoadingContentHistory
                    ? "Chargement..."
                    : `${contentHistory?.revisions.length ?? 0} révision(s)`}
                </dd>
              </div>
              <div>
                <dt>Stockage estimé</dt>
                <dd>
                  {contentHistory?.storageEstimate
                    ? `${formatBytes(
                        contentHistory.storageEstimate.rawStateBytes +
                          contentHistory.storageEstimate.objectVcsMetadataBytes +
                          contentHistory.storageEstimate.blobBytes,
                      )} / ${contentHistory.storageEstimate.documentCount} document(s)`
                    : "Non disponible"}
                </dd>
              </div>
              <div>
                <dt>Plan GC</dt>
                <dd>
                  {contentHistory?.garbageCollectionPlan
                    ? `${contentHistory.garbageCollectionPlan.deletableRevisions.length} révision(s) candidate(s), ${contentHistory.garbageCollectionPlan.orphanBlobs.length} blob(s) orphelin(s), ${formatBytes(contentHistory.garbageCollectionPlan.estimatedFreedStorage.bytes)} libérables`
                    : "Non disponible"}
                </dd>
              </div>
            </dl>
            <div className="actions">
              <button
                className="button button-primary"
                disabled={isSyncingContent}
                onClick={() => void handleSyncContentSnapshot()}
                type="button"
              >
                {isSyncingContent ? "Synchronisation..." : "Créer un snapshot"}
              </button>
              <button
                className="button button-secondary"
                disabled={isLoadingContentHistory}
                onClick={() => void refreshContentHistory(selectedFormation.id)}
                type="button"
              >
                Actualiser maintenance
              </button>
            </div>
            {contentHistory?.branches.length ? (
              <div className="maintenance-block">
                <h3>Branches</h3>
                <ul className="compact-list">
                  {contentHistory.branches.map((branch) => (
                    <li key={branch.name}>
                      <span>{branch.name}</span>
                      <small>
                        HEAD {branch.headRevision ? `#${branch.headRevision}` : "vide"} - {branch.status}
                      </small>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {contentHistory?.tags.length ? (
              <div className="maintenance-block">
                <h3>Tags</h3>
                <ul className="compact-list">
                  {contentHistory.tags.map((tag) => {
                    const isProtected =
                      tag.name === CURRENT_PUBLICATION_TAG ||
                      contentHistory.tags.some(
                        (candidate) =>
                          candidate.name === CURRENT_PUBLICATION_TAG &&
                          candidate.revision === tag.revision,
                      );

                    return (
                      <li key={tag.name}>
                        <span>{tag.name}</span>
                        <small>Révision #{tag.revision}</small>
                        <button
                          className="button button-secondary button-small"
                          disabled={isDeletingTag || isProtected}
                          onClick={() =>
                            void handleDeleteContentTag(tag.name, tag.revision)
                          }
                          type="button"
                        >
                          Supprimer
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            <p className="hint">
              L'exécution de garbage collection reste réservée à un backend
              trusted ou une Cloud Function.
            </p>
          </article>
          ) : null}

          {activeDetailTab === "rights" ? (
          <article className="panel panel-wide">
            <p className="eyebrow">Droits formation</p>
            <h2>Administrateurs de formation</h2>
            <form className="inline-form" onSubmit={handleAddAdmin}>
              <label className="field">
                <span>UID administrateur</span>
                <input
                  onChange={(event) => setAdminUid(event.target.value)}
                  placeholder="uid Firebase Auth"
                  type="text"
                  value={adminUid}
                />
              </label>
              <button
                className="button button-primary"
                disabled={isSaving || !adminUid.trim()}
                type="submit"
              >
                Ajouter
              </button>
            </form>
            {admins.length > 0 ? (
              <ul className="admin-list">
                {admins.map((admin) => (
                  <li key={admin.uid}>
                    <div>
                      <strong>{admin.uid}</strong>
                      <span>Ajouté le {formatDate(admin.createdAt)}</span>
                    </div>
                    <button
                      className="button button-secondary"
                      disabled={isSaving}
                      onClick={() => void handleRemoveAdmin(admin.uid)}
                      type="button"
                    >
                      Retirer
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Aucun administrateur additionnel.</p>
            )}
          </article>
          ) : null}

          {activeDetailTab === "content" ? (
            <PedagogicalContentWorkspace formation={selectedFormation} />
          ) : null}
        </>
      ) : (
        <article className="panel panel-wide">
          <p className="eyebrow">Détail</p>
          <h2>Aucune formation sélectionnée</h2>
          <p>Ouvrez une formation depuis la liste pour accéder aux contenus, droits et snapshots.</p>
        </article>
      )}

      {editorMode ? (
        <div className="modal-backdrop">
          <section
            aria-labelledby="formation-editor-title"
            aria-modal="true"
            className="modal-panel"
            role="dialog"
          >
            <div className="section-heading">
              <div>
                <p className="eyebrow">Métadonnées</p>
                <h2 id="formation-editor-title">
                  {editorMode === "create" ? "Nouvelle formation" : "Modifier la formation"}
                </h2>
              </div>
              <button
                className="button button-secondary button-small"
                disabled={isSaving}
                onClick={closeEditor}
                type="button"
              >
                Fermer
              </button>
            </div>
            <form
              className="stack"
              onSubmit={
                editorMode === "create" ? handleCreateFormation : handleUpdateFormation
              }
            >
              <label className="field">
                <span>Titre</span>
                <input
                  disabled={editorMode === "create" && !mayCreateFormation}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                  type="text"
                  value={title}
                />
              </label>
              <label className="field">
                <span>Description</span>
                <textarea
                  disabled={editorMode === "create" && !mayCreateFormation}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={5}
                  value={description}
                />
              </label>
              <div className="actions">
                <button
                  className="button button-primary"
                  disabled={
                    isSaving || (editorMode === "create" && !mayCreateFormation)
                  }
                  type="submit"
                >
                  {editorMode === "create" ? "Créer" : "Enregistrer"}
                </button>
                {editorMode === "edit" ? (
                  <button
                    className="button button-secondary"
                    disabled={isSaving || !maySoftDeleteFormation}
                    onClick={() => void handleSoftDeleteFormation(selectedFormation)}
                    type="button"
                  >
                    Supprimer logiquement
                  </button>
                ) : null}
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </section>
  );
}
